import { useMemo, useEffect, useState } from 'react';
// @ts-expect-error — нет official @types для react-cytoscapejs
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
// @ts-expect-error — нет official types
import coseBilkent from 'cytoscape-cose-bilkent';
import { api } from '../lib/api';

let registered = false;
function ensureLayoutRegistered() {
  if (!registered) {
    cytoscape.use(coseBilkent);
    registered = true;
  }
}

interface DocumentGraphProps {
  focusDocId: string;
  onOpenDoc: (docId: string) => void;
}

type Depth = 1 | 2 | 3;

export function DocumentGraph({ focusDocId, onOpenDoc }: DocumentGraphProps) {
  useEffect(() => {
    ensureLayoutRegistered();
  }, []);

  const [depth, setDepth] = useState<Depth>(1);
  const graphQuery = api.documents.graph.useQuery({ docId: focusDocId, depth });

  const elements = useMemo(() => {
    if (!graphQuery.data) return [];
    const nodes = graphQuery.data.nodes.map((n) => ({
      data: { id: n.id, label: n.title, isFocus: n.id === focusDocId },
    }));
    const edges = graphQuery.data.edges
      .filter((e, idx, arr) => arr.findIndex((x) => x.from === e.from && x.to === e.to) === idx)
      .map((e) => ({
        data: { id: `${e.from}-${e.to}`, source: e.from, target: e.to },
      }));
    return [...nodes, ...edges];
  }, [graphQuery.data, focusDocId]);

  if (!graphQuery.data) {
    return (
      <div className="text-xs text-ink-faint_l dark:text-ink-faint_d py-4 text-center">
        Загрузка графа…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 text-[11px]">
        <span className="text-ink-faint_l dark:text-ink-faint_d mr-1">Глубина:</span>
        {([1, 2, 3] as const).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDepth(d)}
            className={`px-2 py-0.5 rounded transition ${
              depth === d
                ? 'bg-accent text-white'
                : 'text-ink-muted_l dark:text-ink-muted_d hover:bg-surface3-light dark:hover:bg-surface3-dark'
            }`}
          >
            {d}
          </button>
        ))}
        <span className="ml-auto text-ink-faint_l dark:text-ink-faint_d">
          {graphQuery.data.nodes.length} узл.
        </span>
      </div>

      {graphQuery.data.nodes.length <= 1 ? (
        <div className="text-xs text-ink-faint_l dark:text-ink-faint_d py-4 text-center px-2">
          Нет связанных документов на этой глубине. Создай ссылки через <code>[[wikilinks]]</code>.
        </div>
      ) : (
        <div className="rounded-lg border border-line-light dark:border-line-dark overflow-hidden bg-canvas-light dark:bg-canvas-dark">
          <CytoscapeComponent
            elements={elements as unknown as cytoscape.ElementDefinition[]}
            style={{ width: '100%', height: 280 }}
            layout={{
              name: 'cose-bilkent',
              animate: true,
              fit: true,
              padding: 24,
              nodeRepulsion: 6000,
              idealEdgeLength: 90,
            } as unknown as cytoscape.LayoutOptions}
            stylesheet={[
              {
                selector: 'node',
                style: {
                  'background-color': '#5b5bd6',
                  'background-opacity': 0.85,
                  label: 'data(label)',
                  color: '#9ca3af',
                  'font-size': 10,
                  'font-family': 'Inter, sans-serif',
                  'text-valign': 'bottom',
                  'text-halign': 'center',
                  'text-margin-y': 6,
                  'text-wrap': 'ellipsis',
                  'text-max-width': '100px',
                  width: 14,
                  height: 14,
                  'border-width': 0,
                },
              },
              {
                selector: 'node[?isFocus]',
                style: {
                  'background-color': '#f59e0b',
                  'background-opacity': 1,
                  width: 22,
                  height: 22,
                  'font-weight': 'bold',
                  color: '#f59e0b',
                  'font-size': 11,
                },
              },
              {
                selector: 'edge',
                style: {
                  'target-arrow-shape': 'triangle',
                  'curve-style': 'bezier',
                  width: 1.2,
                  'line-color': '#5b5bd6',
                  'target-arrow-color': '#5b5bd6',
                  opacity: 0.5,
                },
              },
            ]}
            cy={(cy: cytoscape.Core) => {
              cy.on('tap', 'node', (e: cytoscape.EventObject) => {
                const id = e.target.data('id') as string;
                if (id !== focusDocId) onOpenDoc(id);
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
